/**
 * Shopify Order Details — /bulk/shopify-order-details/:OrderId
 *
 * Read-only full view of one clensta.com (Shopify) website order's row in
 * ClenstaShopifyOrders: order + customer + items + payment, the Unicommerce
 * package and invoice, the ShipShopy shipment / AWB, whether the AWB reached
 * Shopify, courier scans, and the full status history. Opened in a new tab
 * from the "Details" button on ShopifyOrderList.js; the shipping actions
 * (Push to ShipShopy / Assign Courier) stay on the list page.
 */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Box,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import MainCard from "../../ui-component/cards/MainCard";
import SubCard from "../../ui-component/cards/SubCard";
import { gridSpacing } from "../../store/constant";
import { STATUS_META, ucStageLabel } from "./ShopifyOrderList";
import { canAccessShopifyOrders, canEditShopifyAddresses } from "./shopifyOrderAccess";
import ShopifyAddressDialog from "./ShopifyAddressDialog";

const API = `${process.env.REACT_APP_AI_PATH}/api/ai/shopify-orders`;

const fmt = (d) => (d ? moment(d).format("DD/MM/YYYY hh:mm A") : "—");

// WhatsApp order updates (whatsapp.<key> on the row) in the order they go out.
const WA_EVENTS = [
  ["order_placed", "Order placed"],
  ["ready_to_ship", "Ready to ship (invoice generated)"],
  ["shipped", "Shipped + tracking link"],
  ["out_for_delivery", "Out for delivery"],
  ["delivered", "Delivered"],
];

function Field({ label, children }) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="textSecondary" display="block">{label}</Typography>
      <Typography variant="body2" style={{ wordBreak: "break-word" }}>{children || children === 0 ? children : "—"}</Typography>
    </Grid>
  );
}

// Access gate (admins + allowlist) — same pattern as ShopifyOrderList.
export default function ShopifyOrderDetails() {
  if (!canAccessShopifyOrders()) {
    return <MainCard><Typography color="error">You do not have access to Shopify Orders.</Typography></MainCard>;
  }
  return <ShopifyOrderDetailsPage />;
}

function ShopifyOrderDetailsPage() {
  const { OrderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [editingAddress, setEditingAddress] = useState(false);

  const load = () =>
    axios
      .get(`${API}/${OrderId}`)
      .then((res) => {
        if (res.data?.success) setOrder(res.data.order);
        else setError(res.data?.message || "Order not found");
      })
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [OrderId]);

  const refresh = () => {
    setBusy("refresh");
    axios
      .post(`${API}/${OrderId}/refresh`)
      .then(() => load())
      .catch((e) => alert(e.response?.data?.message || e.message))
      .finally(() => setBusy(""));
  };

  const downloadInvoice = () => {
    setBusy("invoice");
    axios
      .get(`${API}/${OrderId}/invoice`, { responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `Invoice-${order.orderName}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => alert("Could not download the invoice from Unicommerce"))
      .finally(() => setBusy(""));
  };

  const downloadLabel = () => {
    setBusy("label");
    axios
      .get(`${API}/${OrderId}/label`)
      .then((res) => {
        if (res.data?.success && res.data.url) window.open(res.data.url, "_blank", "noopener,noreferrer");
        else alert(res.data?.message || "Label not available");
      })
      .catch((e) => alert(e.response?.data?.message || e.message))
      .finally(() => setBusy(""));
  };

  if (loading) return <MainCard><Box textAlign="center" p={4}><CircularProgress /></Box></MainCard>;
  if (error || !order) return <MainCard><Typography color="error">{error || "Order not found"}</Typography></MainCard>;

  const meta = STATUS_META[order.currentStatus] || { label: order.currentStatus, color: "#9e9e9e" };
  const a = order.shippingAddress || {};
  const uc = order.unicommerce || {};
  const ss = order.shipshopy || {};
  const sync = order.shopifySync || {};
  const tr = order.tracking || {};
  const pay = order.payment || {};
  const itemsTotal = (order.items || []).reduce((s, i) => s + i.price * i.quantity - (i.discount || 0), 0);

  return (
    <MainCard
      title={
        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
          <Typography variant="h3">Shopify Order {order.orderName}</Typography>
          <Chip label={meta.label} style={{ backgroundColor: meta.color, color: "#fff" }} />
          {order.shopifyCancelledAt && <Chip variant="outlined" label={`Cancelled on Shopify ${fmt(order.shopifyCancelledAt)}`} style={{ borderColor: "#d32f2f", color: "#d32f2f" }} />}
        </Box>
      }
      secondary={
        <Box display="flex" style={{ gap: 8 }}>
          <LoadingButton size="small" variant="outlined" loading={busy === "invoice"} disabled={!order.invoice?.code} onClick={downloadInvoice}>Invoice PDF</LoadingButton>
          <LoadingButton size="small" variant="outlined" loading={busy === "label"} disabled={!ss.awbNumber} onClick={downloadLabel}>ShipShopy Label</LoadingButton>
          {order.canEditAddress && canEditShopifyAddresses() && (
            <Button size="small" variant="outlined" onClick={() => setEditingAddress(true)}>Edit address</Button>
          )}
          <LoadingButton size="small" variant="contained" loading={busy === "refresh"} onClick={refresh}>Refresh</LoadingButton>
        </Box>
      }
    >
      {order.lastError?.message && (
        <Box mb={2} p={1.5} style={{ background: "#fdecea", borderRadius: 6 }}>
          <Typography variant="body2" color="error">
            Last error ({order.lastError.step}, {fmt(order.lastError.at)}): {order.lastError.message}
          </Typography>
        </Box>
      )}

      <Grid container spacing={gridSpacing}>
        <Grid item xs={12} md={6}>
          <SubCard title="Order">
            <Grid container spacing={2}>
              <Field label="Order ID">{order.orderName}</Field>
              <Field label="Shopify order no. (Unicommerce order code)">{order.shopifyOrderId}</Field>
              <Field label="Placed on">{fmt(order.shopifyCreatedAt)}</Field>
              <Field label="Shopify tags">{order.shopifyTags}</Field>
            </Grid>
          </SubCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SubCard title="Customer & Shipping Address">
            <Grid container spacing={2}>
              <Field label="Name">{order.customer?.name}</Field>
              <Field label="Phone">{order.customer?.phone}</Field>
              <Field label="Email">{order.customer?.email}</Field>
              <Field label="City / State / Pincode">{[a.city, a.state, a.pincode].filter(Boolean).join(", ")}</Field>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary" display="block">Address</Typography>
                <Typography variant="body2">{[a.address1, a.address2].filter(Boolean).join(", ") || "—"}</Typography>
              </Grid>
            </Grid>
          </SubCard>
        </Grid>

        <Grid item xs={12}>
          <SubCard title="Products" content={false}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>SKU / EAN</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price (₹)</TableCell>
                    <TableCell align="right">Discount (₹)</TableCell>
                    <TableCell align="right">Net (₹)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(order.items || []).map((i, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{i.name}</TableCell>
                      <TableCell>{i.sku}</TableCell>
                      <TableCell align="right">{i.quantity}</TableCell>
                      <TableCell align="right">{i.price}</TableCell>
                      <TableCell align="right">{i.discount || 0}</TableCell>
                      <TableCell align="right">{(i.price * i.quantity - (i.discount || 0)).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={5} align="right">Items total</TableCell>
                    <TableCell align="right">{itemsTotal.toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} align="right">Shipping charge</TableCell>
                    <TableCell align="right">{(pay.shippingCharge || 0).toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} align="right"><b>Order total</b></TableCell>
                    <TableCell align="right"><b>₹{pay.totalAmount}</b></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </SubCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SubCard title="Payment">
            <Grid container spacing={2}>
              <Field label="Payment mode">{pay.type}</Field>
              <Field label="Order total">₹{pay.totalAmount}</Field>
              <Field label="To collect on delivery">₹{pay.codAmount || 0}</Field>
              <Field label="Total discount">₹{pay.totalDiscount || 0}</Field>
              <Field label="Gateway">{(pay.gateways || []).join(", ")}</Field>
            </Grid>
          </SubCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SubCard title="Unicommerce">
            <Grid container spacing={2}>
              <Field label="Stage">{ucStageLabel(uc, order.invoice)}</Field>
              <Field label="Order status / Package status">{[uc.saleOrderStatus, uc.packageStatus].filter(Boolean).join(" / ")}</Field>
              <Field label="Package code">{uc.packageCode}</Field>
              <Field label="Facility">{uc.facilityCode}</Field>
              <Field label="Invoice no.">{order.invoice?.code}</Field>
              <Field label="Invoice date">{fmt(order.invoice?.date)}</Field>
              <Field label="Courier / AWB on Unicommerce">{uc.trackingNumber ? `${uc.shippingProvider || ""} ${uc.trackingNumber}` : ""}</Field>
              <Field label="AWB pushed by us">{uc.awbPushed ? `Yes — ${fmt(uc.awbPushedAt)}` : "No"}</Field>
              <Field label="Manifest">{uc.manifestCode}</Field>
              <Field label="Dispatched on">{fmt(uc.dispatchedAt)}</Field>
              <Field label="Weight (g)">{uc.weight}</Field>
              <Field label="Last checked">{fmt(uc.lastSyncedAt)}</Field>
            </Grid>
          </SubCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SubCard title="ShipShopy">
            <Grid container spacing={2}>
              <Field label="ShipShopy order ID">{ss.orderId}</Field>
              <Field label="Reference ID">{ss.referenceId}</Field>
              <Field label="Courier">{ss.courierName}</Field>
              <Field label="Shipping charge">{ss.shippingCharge ? `₹${ss.shippingCharge}` : ""}</Field>
              <Field label="AWB number">
                {ss.awbNumber ? <a href={ss.trackingUrl} target="_blank" rel="noopener noreferrer">{ss.awbNumber}</a> : ""}
              </Field>
              <Field label="ShipShopy status">{ss.status}</Field>
              <Field label="Pushed on">{fmt(ss.pushedAt)}</Field>
              <Field label="AWB assigned on">{fmt(ss.awbAssignedAt)}</Field>
            </Grid>
          </SubCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SubCard title="Shopify (website) tracking">
            <Grid container spacing={2}>
              <Field label="AWB showing on Shopify">{sync.synced ? "Yes" : "No"}</Field>
              <Field label="Shopify shipping status">{sync.fulfillmentStatus}</Field>
              <Field label="Tracking number on Shopify">{sync.trackingNumber}</Field>
              <Field label="Courier name on Shopify">{sync.trackingCompany}</Field>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary" display="block">Tracking link on Shopify</Typography>
                {sync.trackingUrl
                  ? <a href={sync.trackingUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: "break-all" }}>{sync.trackingUrl}</a>
                  : <Typography variant="body2">—</Typography>}
              </Grid>
              <Field label="Last checked">{fmt(sync.checkedAt)}</Field>
              <Field label="Status sent to Shopify">{sync.lastEventStatus ? `${sync.lastEventStatus} — ${fmt(sync.lastEventAt)}` : ""}</Field>
              <Field label="Shopify update error">{sync.writeError}</Field>
            </Grid>
          </SubCard>
        </Grid>

        <Grid item xs={12}>
          <SubCard title="WhatsApp updates sent to buyer" content={false}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Message</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {WA_EVENTS.map(([key, label]) => {
                    const w = order.whatsapp?.[key];
                    return (
                      <TableRow key={key}>
                        <TableCell>{label}</TableCell>
                        <TableCell style={{ color: w?.status === "sent" ? "#2e7d32" : w?.status === "failed" ? "#d32f2f" : undefined }}>
                          {w ? w.status : "Not sent"}
                        </TableCell>
                        <TableCell>{fmt(w?.at)}</TableCell>
                        <TableCell>{w?.template || "—"}</TableCell>
                        <TableCell>{w?.error || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </SubCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SubCard title="Courier tracking" content={false}>
            <Box p={2}>
              <Grid container spacing={2}>
                <Field label="Courier status">{tr.courierStatus}</Field>
                <Field label="Status time">{fmt(tr.statusTime)}</Field>
                <Field label="Expected delivery">{tr.expectedDelivery}</Field>
                <Field label="Out-for-delivery attempts">{tr.outForDeliveryCount}</Field>
                <Field label="Delivered on">{fmt(tr.deliveredAt)}</Field>
                <Field label="Last checked">{fmt(tr.lastCheckedAt)}</Field>
              </Grid>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Scan time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Location</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(tr.scanDetail || []).length === 0 && (
                    <TableRow><TableCell colSpan={3} align="center">No courier scans yet</TableCell></TableRow>
                  )}
                  {(tr.scanDetail || []).map((s, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{s.date}</TableCell>
                      <TableCell>{s.status}</TableCell>
                      <TableCell>{s.location}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </SubCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SubCard title="Status history" content={false}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(order.statusHistory || []).map((h, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{fmt(h.at)}</TableCell>
                      <TableCell>{STATUS_META[h.status]?.label || h.status}</TableCell>
                      <TableCell>{h.note}</TableCell>
                      <TableCell>{h.by || "system"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </SubCard>
        </Grid>
      </Grid>

      <Box mt={2}>
        <Button size="small" onClick={() => window.close()}>Close</Button>
      </Box>

      {editingAddress && (
        <ShopifyAddressDialog orderId={OrderId} onClose={() => setEditingAddress(false)} onSaved={() => load()} />
      )}
    </MainCard>
  );
}
